import React, { useState, useEffect, useMemo, useContext } from "react"
import PropTypes from "prop-types"
import { API } from "aws-amplify"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material"
import IconButton from "@mui/material/IconButton"
import CheckRoundedIcon from "@mui/icons-material/CheckRounded"
import CloseRoundedIcon from "@mui/icons-material/CloseRounded"
import CloseIcon from "@mui/icons-material/Close"
import i18n from "i18n"
import AuthContext from "context/auth/authContext"
import {
  normalizeSpanishTaxId,
  isValidSpanishTaxId,
  splitFullName,
} from "../../utils/invoicingSpainUtils"

const REQUIRED_PARTY_FIELDS = [
  "taxIdCode",
  "name",
  "personGiven",
  "personSurname",
  "personDni",
  "addrStreet",
  "addrLocality",
  "addrRegion",
  "addrCode",
  "email",
]

const InvoicingSpainModal = ({
  open,
  onClose,
  linkUrl,
  organizationFields,
  hasExistingParty,
  onPartyRegistered,
}) => {
  const { user } = useContext(AuthContext)

  const initialFromContext = useMemo(() => {
    const split = splitFullName(user?.["custom:nombre_apellido"])
    return {
      taxIdCode: organizationFields?.cif_number || "",
      name: organizationFields?.name || "",
      personGiven: split.given,
      personSurname: split.surname,
      personSurname2: split.surname2,
      personDni: user?.["custom:nif"] || "",
      addrStreet: organizationFields?.address || "",
      addrNum: "",
      addrLocality: "",
      addrRegion: "",
      addrCode: "",
      email: user?.email || "",
    }
  }, [organizationFields, user])

  // step 0: form, step 1: iframe
  const [activeStep, setActiveStep] = useState(0)
  const [formData, setFormData] = useState(initialFromContext)
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const taxIdCodeNormalized = normalizeSpanishTaxId(formData.taxIdCode)
  const taxIdCodeIsValid = taxIdCodeNormalized
    ? isValidSpanishTaxId(taxIdCodeNormalized)
    : null
  const taxIdCodeAdornment =
    taxIdCodeIsValid === null ? null : (
      <InputAdornment position="end" disablePointerEvents>
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            bgcolor: taxIdCodeIsValid ? "success.main" : "error.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 0.5,
          }}
        >
          {taxIdCodeIsValid ? (
            <CheckRoundedIcon sx={{ fontSize: 14, color: "#fff" }} />
          ) : (
            <CloseRoundedIcon sx={{ fontSize: 14, color: "#fff" }} />
          )}
        </Box>
      </InputAdornment>
    )

  // Re-init form when dialog opens (prefill with latest context).
  useEffect(() => {
    if (open) {
      setFormData(initialFromContext)
      setErrors({})
      setSubmitError(null)
      // If there is already a registered party (we have a linkUrl), skip the form
      // and jump straight to the iframe step.
      setActiveStep(hasExistingParty && linkUrl ? 1 : 0)
    }
  }, [open, initialFromContext, hasExistingParty, linkUrl])

  const handleFieldChange = field => event => {
    const value = event.target.value
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const validate = () => {
    const next = {}
    REQUIRED_PARTY_FIELDS.forEach(field => {
      if (!formData[field] || !String(formData[field]).trim()) {
        next[field] = i18n.t("invoicingSpainWizard.fieldRequired")
      }
    })
    if (
      formData.taxIdCode &&
      taxIdCodeNormalized &&
      !isValidSpanishTaxId(taxIdCodeNormalized)
    ) {
      next.taxIdCode = i18n.t("invoicingSpainWizard.invalidTaxIdCode")
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      next.email = i18n.t("invoicingSpainWizard.invalidEmail")
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const buildPartyPayload = () => ({
    data: {
      organizationId: organizationFields.organizationId,
    },
    party: {
      tax_id: {
        country: "ES",
        code: taxIdCodeNormalized || formData.taxIdCode.trim(),
      },
      name: formData.name.trim(),
      people: [
        {
          name: {
            given: formData.personGiven.trim(),
            surname: formData.personSurname.trim(),
            surname2: formData.personSurname2.trim(),
          },
          role: "",
          identities: [
            {
              country: "ES",
              key: "dni",
              code: formData.personDni.trim(),
            },
          ],
        },
      ],
      addresses: [
        {
          street: formData.addrStreet.trim(),
          num: formData.addrNum.trim(),
          locality: formData.addrLocality.trim(),
          region: formData.addrRegion.trim(),
          code: formData.addrCode.trim(),
          country: "ES",
        },
      ],
      emails: [{ addr: formData.email.trim() }],
    },
  })

  const handleNext = async () => {
    if (!validate()) return
    setSubmitError(null)
    setSubmitting(true)
    try {
      const body = buildPartyPayload()
      await API.post("CORE", "/invopop/verifactu/register_party", { body })
      // Tell parent to refetch and obtain link_url
      const updated = await onPartyRegistered?.()
      if (updated?.link_url) {
        setActiveStep(1)
      } else {
        // Even without a link_url we move on; the iframe step will simply be empty.
        setActiveStep(1)
      }
    } catch (error) {
      const data = error?.response?.data
      console.error(
        "Error registering party:",
        data?.reason || error?.message || "unknown error",
      )
      const code = data?.code
      setSubmitError(
        code
          ? i18n.t(`invoicingSpainWizard.errors.${code}`, {
              defaultValue: i18n.t("invoicingSpainWizard.submitError"),
            })
          : i18n.t("invoicingSpainWizard.submitError"),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const renderField = (field, labelKey, opts = {}) => (
    <TextField
      fullWidth
      size="small"
      label={i18n.t(`invoicingSpainWizard.${labelKey}`)}
      value={formData[field] || ""}
      onChange={handleFieldChange(field)}
      error={Boolean(errors[field])}
      helperText={errors[field] || opts.helperText || ""}
      disabled={submitting}
      required={REQUIRED_PARTY_FIELDS.includes(field)}
      {...opts.tfProps}
      InputProps={
        field === "taxIdCode"
          ? {
              ...opts.tfProps?.InputProps,
              endAdornment: taxIdCodeAdornment,
            }
          : opts.tfProps?.InputProps
      }
    />
  )

  const formStep = (
    <Box sx={{ pt: 1 }}>
      <Box sx={{ mb: 2, color: "#6d6d6d", fontSize: "14px" }}>
        {i18n.t("invoicingSpainWizard.formIntro")}
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {renderField("taxIdCode", "taxIdCode")}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderField("name", "companyName")}
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ fontWeight: 600, mt: 1 }}>
            {i18n.t("invoicingSpainWizard.legalRepresentative")}
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          {renderField("personGiven", "given")}
        </Grid>
        <Grid item xs={12} sm={4}>
          {renderField("personSurname", "surname")}
        </Grid>
        <Grid item xs={12} sm={4}>
          {renderField("personSurname2", "surname2")}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderField("personDni", "dni")}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderField("email", "email")}
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ fontWeight: 600, mt: 1 }}>
            {i18n.t("invoicingSpainWizard.fiscalAddress")}
          </Box>
        </Grid>
        <Grid item xs={12} sm={8}>
          {renderField("addrStreet", "street")}
        </Grid>
        <Grid item xs={12} sm={4}>
          {renderField("addrNum", "num")}
        </Grid>
        <Grid item xs={12} sm={4}>
          {renderField("addrCode", "postalCode")}
        </Grid>
        <Grid item xs={12} sm={4}>
          {renderField("addrLocality", "locality")}
        </Grid>
        <Grid item xs={12} sm={4}>
          {renderField("addrRegion", "region")}
        </Grid>
      </Grid>
      {submitError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {submitError}
        </Alert>
      ) : null}
    </Box>
  )

  const isSafeInvopopUrl = linkUrl
    ? /^https:\/\/([a-z0-9-]+\.)*invopop\.com(\/|$)/i.test(linkUrl)
    : false

  const iframeStep = (
    <Box sx={{ width: "100%", height: "600px" }}>
      {isSafeInvopopUrl ? (
        <iframe
          src={linkUrl}
          width="100%"
          height="100%"
          style={{ border: "none" }}
          title="Integracion con Facturación electronica España"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      ) : (
        <Box sx={{ p: 4, textAlign: "center", color: "#6d6d6d" }}>
          {i18n.t("invoicingSpainWizard.noLinkUrl")}
        </Box>
      )}
    </Box>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {i18n.t("invoicingSpainWizard.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>{i18n.t("invoicingSpainWizard.step1")}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{i18n.t("invoicingSpainWizard.step2")}</StepLabel>
          </Step>
        </Stepper>
        {activeStep === 0 ? formStep : iframeStep}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {activeStep === 0 ? (
          <>
            <Button onClick={onClose} disabled={submitting}>
              {i18n.t("invoicingSpainWizard.cancel")}
            </Button>
            <Button
              onClick={handleNext}
              color="branded"
              variant="contained"
              disabled={submitting}
            >
              {submitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                i18n.t("invoicingSpainWizard.next")
              )}
            </Button>
          </>
        ) : (
          <Button onClick={onClose} color="branded" variant="contained">
            {i18n.t("invoicingSpainWizard.close")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

InvoicingSpainModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  linkUrl: PropTypes.string,
  organizationFields: PropTypes.object.isRequired,
  hasExistingParty: PropTypes.bool,
  onPartyRegistered: PropTypes.func,
}

InvoicingSpainModal.defaultProps = {
  linkUrl: null,
  hasExistingParty: false,
  onPartyRegistered: undefined,
}

export default InvoicingSpainModal
